// src/pages/Organizations/Marketing/components/CampaignManager.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  Divider,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Chip,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tab,
  Tabs,
  LinearProgress,
  SelectChangeEvent,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AssessmentIcon from '@mui/icons-material/Assessment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { v4 as uuidv4 } from 'uuid';
import dayjs, { Dayjs } from 'dayjs';
import { useMarketingStudio, Campaign, MarketingAsset } from '../../../../contexts/MarketingStudioContext';

interface CampaignManagerProps {
  organizationId: string;
  organizationName: string;
}

interface CampaignFormData {
  name: string;
  description: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  platforms: string[];
  status: 'draft' | 'scheduled' | 'active' | 'completed' | 'archived';
  content: {
    title: string;
    description: string;
    callToAction: string;
  };
  schedule: {
    frequency: 'once' | 'daily' | 'weekly' | 'monthly';
    times: string[];
  };
  selectedAssets: string[];
}

const platformOptions = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'twitter', label: 'Twitter' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'email', label: 'Email' },
  { value: 'web', label: 'Website' },
];

const frequencyOptions = [
  { value: 'once', label: 'Once' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const statusColors = {
  draft: '#9e9e9e',
  scheduled: '#2196f3',
  active: '#4caf50',
  completed: '#8bc34a',
  archived: '#757575',
};

const CampaignManager: React.FC<CampaignManagerProps> = ({ organizationId, organizationName }) => {
  const theme = useTheme();
  const { campaigns, addCampaign, updateCampaign, deleteCampaign, marketingAssets } = useMarketingStudio();
  
  // Campaign management state
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentCampaignId, setCurrentCampaignId] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  
  // Default form data
  const defaultFormData: CampaignFormData = {
    name: '',
    description: '',
    startDate: dayjs(),
    endDate: dayjs().add(30, 'day'),
    platforms: ['facebook', 'instagram'],
    status: 'draft',
    content: {
      title: '',
      description: '',
      callToAction: '',
    },
    schedule: {
      frequency: 'once',
      times: [new Date().toISOString()],
    },
    selectedAssets: [],
  };
  
  // Form state
  const [formData, setFormData] = useState<CampaignFormData>(defaultFormData);
  
  // Filtered campaigns based on tab
  const filteredCampaigns = campaigns.filter(campaign => {
    if (campaign.organizationId !== organizationId) return false;
    
    switch (tabValue) {
      case 0: // All
        return true;
      case 1: // Active
        return campaign.status === 'active';
      case 2: // Scheduled
        return campaign.status === 'scheduled';
      case 3: // Draft
        return campaign.status === 'draft';
      case 4: // Completed
        return campaign.status === 'completed' || campaign.status === 'archived';
      default:
        return true;
    }
  });
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Open campaign dialog for creating a new campaign
  const handleOpenCreateDialog = () => {
    setFormData(defaultFormData);
    setIsEditMode(false);
    setCurrentCampaignId(null);
    setCampaignDialogOpen(true);
  };
  
  // Open campaign dialog for editing an existing campaign
  const handleOpenEditDialog = (campaign: Campaign) => {
    setFormData({
      name: campaign.name,
      description: campaign.description,
      startDate: dayjs(campaign.startDate),
      endDate: dayjs(campaign.endDate),
      platforms: campaign.platforms,
      status: campaign.status,
      content: { ...campaign.content },
      schedule: { ...campaign.schedule },
      selectedAssets: campaign.assets || [],
    });
    setIsEditMode(true);
    setCurrentCampaignId(campaign.id);
    setCampaignDialogOpen(true);
  };
  
  // Close campaign dialog
  const handleCloseDialog = () => {
    setCampaignDialogOpen(false);
  };
  
  // Open asset selection dialog
  const handleOpenAssetDialog = () => {
    setAssetDialogOpen(true);
  };
  
  // Close asset selection dialog
  const handleCloseAssetDialog = () => {
    setAssetDialogOpen(false);
  };
  
  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Handle content input changes
  const handleContentChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        [name]: value,
      },
    }));
  };
  
  // Handle platforms selection
  const handlePlatformsChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    setFormData(prev => ({
      ...prev,
      platforms: typeof value === 'string' ? value.split(',') : value,
    }));
  };
  
  // Handle frequency change
  const handleFrequencyChange = (event: SelectChangeEvent) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        frequency: event.target.value as 'once' | 'daily' | 'weekly' | 'monthly',
      },
    }));
  };
  
  // Handle status change
  const handleStatusChange = (event: SelectChangeEvent) => {
    setFormData(prev => ({
      ...prev,
      status: event.target.value as 'draft' | 'scheduled' | 'active' | 'completed' | 'archived',
    }));
  };
  
  // Handle date changes
  const handleStartDateChange = (date: Dayjs | null) => {
    setFormData(prev => ({ ...prev, startDate: date }));
  };
  
  const handleEndDateChange = (date: Dayjs | null) => {
    setFormData(prev => ({ ...prev, endDate: date }));
  };
  
  // Handle asset selection
  const handleAssetSelect = (assetId: string) => {
    setFormData(prev => {
      const isSelected = prev.selectedAssets.includes(assetId);
      
      return {
        ...prev,
        selectedAssets: isSelected
          ? prev.selectedAssets.filter(id => id !== assetId)
          : [...prev.selectedAssets, assetId],
      };
    });
  };
  
  // Handle form submission
  const handleSubmit = () => {
    if (!formData.name || !formData.startDate || !formData.endDate) {
      // Show validation errors
      return;
    }
    
    if (isEditMode && currentCampaignId) {
      // Update existing campaign
      updateCampaign(currentCampaignId, {
        name: formData.name,
        description: formData.description,
        startDate: formData.startDate.toDate().toISOString(),
        endDate: formData.endDate.toDate().toISOString(),
        platforms: formData.platforms,
        status: formData.status,
        content: formData.content,
        schedule: formData.schedule,
        assets: formData.selectedAssets,
        updatedAt: new Date().toISOString(),
      });
    } else {
      // Create new campaign
      const newCampaign: Campaign = {
        id: uuidv4(),
        name: formData.name,
        description: formData.description,
        organizationId,
        startDate: formData.startDate.toDate().toISOString(),
        endDate: formData.endDate.toDate().toISOString(),
        status: formData.status,
        platforms: formData.platforms,
        assets: formData.selectedAssets,
        content: formData.content,
        schedule: formData.schedule,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      
      addCampaign(newCampaign);
    }
    
    handleCloseDialog();
  };
  
  // Handle campaign deletion
  const handleDeleteCampaign = (campaignId: string) => {
    deleteCampaign(campaignId);
  };
  
  // Get campaign progress percentage
  const getCampaignProgress = (campaign: Campaign) => {
    if (campaign.status === 'completed') return 100;
    if (campaign.status === 'draft') return 0;
    
    const startDate = new Date(campaign.startDate).getTime();
    const endDate = new Date(campaign.endDate).getTime();
    const currentDate = new Date().getTime();
    
    if (currentDate < startDate) return 0;
    if (currentDate > endDate) return 100;
    
    const totalDuration = endDate - startDate;
    const elapsedDuration = currentDate - startDate;
    
    return Math.round((elapsedDuration / totalDuration) * 100);
  };
  
  // Get available assets for this organization
  const availableAssets = marketingAssets.filter(asset => asset.organizationId === organizationId);
  
  // Get status chip color
  const getStatusColor = (status: string) => {
    return statusColors[status as keyof typeof statusColors] || theme.palette.primary.main;
  };
  
  return (
    <Box>
      {/* Campaign Tabs and Add Button */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2
      }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          aria-label="campaign tabs"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <Tab label="All Campaigns" />
          <Tab label="Active" />
          <Tab label="Scheduled" />
          <Tab label="Drafts" />
          <Tab label="Completed" />
        </Tabs>
        
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          New Campaign
        </Button>
      </Box>
      
      {/* Campaign Cards */}
      {filteredCampaigns.length === 0 ? (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            textAlign: 'center', 
            borderRadius: 2,
            border: `1px dashed ${theme.palette.divider}`
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No campaigns found
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Create your first marketing campaign to begin promoting your organization.
          </Typography>
          <Button 
            variant="outlined" 
            startIcon={<AddIcon />} 
            onClick={handleOpenCreateDialog}
          >
            Create Campaign
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredCampaigns.map((campaign) => (
            <Grid item xs={12} md={6} lg={4} key={campaign.id}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: 'none',
                borderRadius: 2
              }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="div">
                      {campaign.name}
                    </Typography>
                    <Chip 
                      label={campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)} 
                      size="small"
                      sx={{ 
                        bgcolor: getStatusColor(campaign.status),
                        color: 'white'
                      }}
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {campaign.description}
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Progress
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={getCampaignProgress(campaign)} 
                      sx={{ height: 8, borderRadius: 4, mt: 0.5 }}
                    />
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" display="block" color="text.secondary">
                        Start Date
                      </Typography>
                      <Typography variant="body2">
                        {new Date(campaign.startDate).toLocaleDateString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" display="block" color="text.secondary">
                        End Date
                      </Typography>
                      <Typography variant="body2">
                        {new Date(campaign.endDate).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Platforms
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {campaign.platforms.map((platform) => (
                        <Chip
                          key={platform}
                          label={platform}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  </Box>
                </CardContent>
                
                <CardActions sx={{ justifyContent: 'space-between', p: 2, pt: 0 }}>
                  <Button 
                    size="small" 
                    startIcon={<VisibilityIcon />}
                  >
                    View
                  </Button>
                  <Box>
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenEditDialog(campaign)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDeleteCampaign(campaign.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Campaign Form Dialog */}
      <Dialog 
        open={campaignDialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isEditMode ? 'Edit Campaign' : 'Create New Campaign'}
        </DialogTitle>
        
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Basic Info */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Campaign Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="status-label">Status</InputLabel>
                    <Select
                      labelId="status-label"
                      id="status"
                      value={formData.status}
                      label="Status"
                      onChange={handleStatusChange}
                    >
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="scheduled">Scheduled</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="archived">Archived</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="platforms-label">Platforms</InputLabel>
                    <Select
                      labelId="platforms-label"
                      id="platforms"
                      multiple
                      value={formData.platforms}
                      label="Platforms"
                      onChange={handlePlatformsChange}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {platformOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            
            {/* Schedule */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Schedule
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Start Date"
                      value={formData.startDate}
                      onChange={handleStartDateChange}
                      renderInput={params => (
                        <TextField {...params} size='small' fullWidth />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="End Date"
                      value={formData.endDate}
                      onChange={handleEndDateChange}
                      renderInput={params => (
                        <TextField {...params} size='small' fullWidth />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="frequency-label">Frequency</InputLabel>
                    <Select
                      labelId="frequency-label"
                      id="frequency"
                      value={formData.schedule.frequency}
                      label="Frequency"
                      onChange={handleFrequencyChange}
                    >
                      {frequencyOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            
            {/* Content */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Content
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Content Title"
                    name="title"
                    value={formData.content.title}
                    onChange={handleContentChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Content Description"
                    name="description"
                    value={formData.content.description}
                    onChange={handleContentChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Call to Action"
                    name="callToAction"
                    value={formData.content.callToAction}
                    onChange={handleContentChange}
                  />
                </Grid>
              </Grid>
            </Grid>
            
            {/* Assets */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Selected Assets ({formData.selectedAssets.length})
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAssetDialog}
                >
                  Add Assets
                </Button>
              </Box>
              
              {formData.selectedAssets.length === 0 ? (
                <Box sx={{ 
                  p: 2, 
                  textAlign: 'center', 
                  border: `1px dashed ${theme.palette.divider}`,
                  borderRadius: 1
                }}>
                  <Typography variant="body2" color="text.secondary">
                    No assets selected for this campaign
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ 
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  overflow: 'hidden'
                }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Asset</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.selectedAssets.map((assetId) => {
                          const asset = marketingAssets.find(a => a.id === assetId);
                          if (!asset) return null;
                          
                          return (
                            <TableRow key={asset.id}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {asset.type === 'image' && (
                                    <Avatar
                                      src={asset.thumbnailUrl}
                                      variant="rounded"
                                      sx={{ width: 40, height: 40, mr: 2 }}
                                    />
                                  )}
                                  <Typography variant="body2">
                                    {asset.name}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={asset.type} 
                                  size="small" 
                                  variant="outlined" 
                                />
                              </TableCell>
                              <TableCell align="right">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleAssetSelect(asset.id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            startIcon={isEditMode ? <CheckCircleIcon /> : <AddIcon />}
          >
            {isEditMode ? 'Update Campaign' : 'Create Campaign'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Asset Selection Dialog */}
      <Dialog
        open={assetDialogOpen}
        onClose={handleCloseAssetDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Assets</DialogTitle>
        <DialogContent dividers>
          {availableAssets.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No assets available
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Create assets in the Asset Generator to use them in campaigns
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {availableAssets.map((asset) => (
                <Grid item xs={12} sm={6} md={4} key={asset.id}>
                  <Card 
                    sx={{ 
                      border: formData.selectedAssets.includes(asset.id)
                        ? `2px solid ${theme.palette.primary.main}`
                        : `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                    onClick={() => handleAssetSelect(asset.id)}
                  >
                    {asset.type === 'image' && (
                      <Box sx={{ position: 'relative' }}>
                        <CardMedia
                          component="img"
                          height="120"
                          image={asset.url}
                          alt={asset.name}
                        />
                        {formData.selectedAssets.includes(asset.id) && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              p: 0.5,
                              bgcolor: theme.palette.primary.main,
                              borderBottomLeftRadius: 8,
                            }}
                          >
                            <CheckCircleIcon sx={{ color: 'white', fontSize: 20 }} />
                          </Box>
                        )}
                      </Box>
                    )}
                    <CardContent>
                      <Typography variant="body2" noWrap>
                        {asset.name}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Chip 
                          label={asset.type} 
                          size="small" 
                          variant="outlined" 
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssetDialog} color="primary">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignManager;
